import React, { useCallback, useEffect, useState } from 'react'
import apiClient from '../../services/api'
import { useToast } from '../../context/ToastContext'
import LoadingSpinner from '../../components/LoadingSpinner'
import { uploadImageToCloudinary } from '../../services/cloudinaryUpload'

const doctorFilters = [
  { key: 'all', label: 'All Doctors' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' }
]

const initialForm = {
  name: '',
  specialty: '',
  experience: '',
  summary: '',
  achievements: '',
  imageUrl: '',
  imagePublicId: '',
  active: true
}

const AdminDoctors = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [form, setForm] = useState(initialForm)
  const [editId, setEditId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const { addToast } = useToast()

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/cms/doctors')
      setItems(res.data)
    } catch (e) {
      addToast('Error loading doctors', 'danger')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    load()
  }, [load])

  const resetForm = () => {
    setForm(initialForm)
    setEditId(null)
  }

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      addToast('Please choose a valid doctor image.', 'warning')
      return
    }

    setUploadingImage(true)

    try {
      const upload = await uploadImageToCloudinary(file, 'medical-clinic/doctors')
      setForm((prev) => ({ ...prev, ...upload }))
      addToast('Doctor image uploaded', 'success')
    } catch (error) {
      addToast('Unable to upload doctor image', 'danger')
    } finally {
      setUploadingImage(false)
    }
  }

  const save = async (e) => {
    e.preventDefault()

    const payload = {
      ...form,
      achievements: form.achievements
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    }

    try {
      if (editId) {
        await apiClient.put(`/api/cms/doctors/${editId}`, payload)
      } else {
        await apiClient.post('/api/cms/doctors', payload)
      }
      addToast(editId ? 'Doctor updated' : 'Doctor added', 'success')
      resetForm()
      load()
    } catch (error) {
      addToast('Error saving doctor profile', 'danger')
    }
  }

  const remove = async (id) => {
    if (!window.confirm('Delete doctor profile?')) return
    await apiClient.delete(`/api/cms/doctors/${id}`)
    addToast('Deleted', 'success')
    load()
  }

  if (loading) return <LoadingSpinner />

  const filteredDoctors = items.filter((item) => {
    if (statusFilter === 'active') return item.active
    if (statusFilter === 'inactive') return !item.active
    return true
  })

  const filterCount = (key) => {
    if (key === 'active') return items.filter((item) => item.active).length
    if (key === 'inactive') return items.filter((item) => !item.active).length
    return items.length
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h2>Doctor Highlights</h2>
          <p>
            Manage the specialist cards shown across the public site, including uploaded photos, achievements, and active visibility.
          </p>
        </div>
      </div>

      <div className="card p-3 mb-4 shadow-sm admin-form-card">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h5 className="mb-0">{editId ? 'Edit Doctor Profile' : 'Add Doctor Profile'}</h5>
          {editId && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
              Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={save} className="row g-3">
        <div className="col-md-6">
          <input
            className="form-control"
            placeholder="Doctor name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </div>
        <div className="col-md-6">
          <input
            className="form-control"
            placeholder="Specialty"
            value={form.specialty}
            onChange={(e) => setForm({ ...form, specialty: e.target.value })}
            required
          />
        </div>
        <div className="col-md-4">
          <input
            className="form-control"
            placeholder="Experience e.g. 12 years"
            value={form.experience}
            onChange={(e) => setForm({ ...form, experience: e.target.value })}
            required
          />
        </div>
        <div className="col-md-8">
          <input
            className="form-control"
            placeholder="Achievements separated by commas"
            value={form.achievements}
            onChange={(e) => setForm({ ...form, achievements: e.target.value })}
            required
          />
        </div>
        <div className="col-12">
          <textarea
            className="form-control"
            rows="3"
            placeholder="Doctor summary"
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            required
          />
        </div>
        <div className="col-md-6">
          <input
            type="file"
            accept="image/*"
            className="form-control"
            onChange={handleImageUpload}
            required={!form.imageUrl}
          />
          <small className="text-muted">Doctor image is required.</small>
        </div>
        <div className="col-md-3 d-flex align-items-center">
          <div className="form-check">
            <input
              type="checkbox"
              id="doctorActive"
              className="form-check-input"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <label htmlFor="doctorActive" className="form-check-label">Active</label>
          </div>
        </div>
        <div className="col-md-3 d-flex align-items-center">
          <button className="btn btn-primary w-100" type="submit" disabled={uploadingImage}>
            {editId ? 'Update Doctor' : 'Add Doctor'}
          </button>
        </div>
        {(form.imageUrl || uploadingImage) && (
          <div className="col-12">
            <div className="card p-3">
              <h5 className="mb-3">Image Preview</h5>
              {uploadingImage ? (
                <p className="mb-0 text-muted">Uploading doctor image...</p>
              ) : (
                <img
                  src={form.imageUrl}
                  alt="Doctor preview"
                  style={{ maxWidth: '100%', maxHeight: '280px', objectFit: 'cover', borderRadius: '0.5rem' }}
                />
              )}
            </div>
          </div>
        )}
        </form>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filter-chips">
          {doctorFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`admin-filter-chip${statusFilter === filter.key ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              <span className="admin-filter-chip-count">{filterCount(filter.key)}</span>
            </button>
          ))}
        </div>
        <div className="admin-note">{filteredDoctors.length} profile{filteredDoctors.length === 1 ? '' : 's'} shown</div>
      </div>

      <div className="row g-3">
        {filteredDoctors.map((item) => (
          <div key={item._id} className="col-md-4">
            <div className="card h-100">
              <img src={item.imageUrl} alt={item.name} className="card-img-top" style={{ height: '220px', objectFit: 'cover' }} />
              <div className="card-body">
                <h5>{item.name}</h5>
                <p className="text-muted mb-1">{item.specialty}</p>
                <p className="mb-1">Experience: {item.experience}</p>
                <p>{item.summary}</p>
                <p className="mb-1"><strong>Achievements:</strong> {(item.achievements || []).join(', ')}</p>
                <span className={`admin-status-badge ${item.active ? 'admin-status-active' : 'admin-status-inactive'}`}>
                  {item.active ? 'Active' : 'Inactive'}
                </span>
                <button
                  className="btn btn-sm btn-secondary me-2"
                  onClick={() => {
                    setForm({
                      name: item.name,
                      specialty: item.specialty,
                      experience: item.experience,
                      summary: item.summary,
                      achievements: (item.achievements || []).join(', '),
                      imageUrl: item.imageUrl,
                      imagePublicId: item.imagePublicId || '',
                      active: item.active
                    })
                    setEditId(item._id)
                  }}
                >
                  Edit
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(item._id)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default AdminDoctors
